import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Icon as LegacyIcon } from '@ant-design/compatible';
import { Button, message, Spin, Card, Modal, Typography, Alert } from 'antd';

import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import { PartItem, PartSlot } from '@partsbadger/library';
import PartStore, { FileProps, is2dFile, is3dFile } from '../../stores/PartStore';
import QuoteStore from '../../stores/QuoteStore';
import { SortFiles } from '../SortFiles';
import { restricted_file_extensions } from '@partsbadger/utils';

interface Props {
    is_staff: boolean | undefined;
}

const { Title, Paragraph, Text } = Typography;

export const AssociateParts = observer((props: Props) => {
    const handleSubmit = () => {
        if (QuoteStore.for_staff) {
            // Staff keep working while the matched parts upload in the background tray.
            PartStore.enqueueUploads(PartStore.fileParts);
            return;
        }
        PartStore.fileParts.map(item => {
            if (item['3D File'] || item['2D File'] || item['Additional File']?.length) {
                PartStore.uploadPart(item);
            } else {
                PartStore.removePart(item); // Remove empty lines
            }
        });
    };

    const handleRemove = (item: FileProps, fileType: '3D File' | '2D File' | 'Additional File', file?: File) => {
        PartStore.removeFile(item, fileType, file);
    };

    // The same lists addPart accepts, so a file that could be dropped can also be moved.
    const check3DAllowedFiles = (item_filename: string) => Boolean(is3dFile(item_filename));

    const check2DAllowedFiles = (item_filename: string) => Boolean(is2dFile(item_filename));

    const parts = PartStore.fileParts.map((part_item, index) => {
        const file2d = part_item['2D File'] ? (
            <PartItem
                index={index}
                file={part_item['2D File']}
                type={'2D File'}
                handleRemove={() => handleRemove(part_item, '2D File')}
            />
        ) : null;

        const file3d = part_item['3D File'] ? (
            <PartItem
                index={index}
                file={part_item['3D File']}
                type={'3D File'}
                handleRemove={() => handleRemove(part_item, '3D File')}
            />
        ) : null;

        const additionalFiles = part_item['Additional File'] || [];
        const fileOthers =
            additionalFiles.length > 0
                ? additionalFiles.map((file: File) => {
                      return (
                          <PartItem
                              key={index + 1}
                              index={index}
                              file={file}
                              type={'Additional File'}
                              handleRemove={() => handleRemove(part_item, 'Additional File', file)}
                          />
                      );
                  })
                : null;

        return (
            <div
                className={'row-associate-parts'}
                data-file-2d={part_item['2D File']?.name}
                data-file-3d={part_item['3D File']?.name}
                data-testid="otf-match-row"
                key={`${part_item.uid}-${index}`}
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    // backgroundColor: 'rgba(217, 217, 217, 0.19)',
                    marginBottom: '5px',
                    padding: '10px',
                    position: 'relative',
                }}
            >
                <LegacyIcon
                    type={'close'}
                    style={{
                        position: 'absolute',
                        right: '5px',
                        top: '5px',
                        cursor: 'pointer',
                        color: '#d92923',
                    }}
                    onClick={() => {
                        PartStore.removePart(part_item);
                    }}
                />

                {part_item.uploadError ? (
                    <Text type="danger" style={{ position: 'absolute', left: '10px', top: '4px', fontSize: 12 }}>
                        Upload failed. Press Continue to try again or remove it.
                    </Text>
                ) : null}

                <PartSlot
                    index={index}
                    accept={['3D File', '2D File', 'Additional File']}
                    lastDroppedItem={file3d}
                    onDrop={item => {
                        if (check3DAllowedFiles(item.file.name)) {
                            PartStore.swapParts(item.index, index, item.type, '3D File', item.file);
                        } else {
                            message.error('The file type is not allowed for the 3D category.', 4);
                        }
                    }}
                    onDropNew={files => {
                        if (files.length) {
                            const f = files.pop();
                            if (f) {
                                if (check3DAllowedFiles(f.name)) {
                                    PartStore.addFile(index, '3D File', f);
                                } else {
                                    message.error('The file type is not allowed for the 3D category.');
                                }
                            }
                        }
                    }}
                />

                <PartSlot
                    index={index}
                    accept={['3D File', '2D File', 'Additional File']}
                    lastDroppedItem={file2d}
                    onDrop={item => {
                        if (check2DAllowedFiles(item.file.name)) {
                            PartStore.swapParts(item.index, index, item.type, '2D File', item.file);
                        } else {
                            message.error('The file type is not allowed for the 2D category.', 4);
                        }
                    }}
                    onDropNew={(files: Array<File>) => {
                        const f = files.pop();
                        if (f) {
                            if (check2DAllowedFiles(f.name)) {
                                PartStore.addFile(index, '2D File', f);
                            } else {
                                message.error('The file type is not allowed for the 2D category.');
                            }
                        }
                    }}
                />
            </div>
        );
    });

    if (!PartStore.hasParts()) {
        return null;
    }

    const content = (
        <React.Fragment>
            <div className="m-auto" data-testid="otf-match-modal">
                <DndProvider backend={HTML5Backend}>
                    <div
                        className="flex flex-row"
                        style={
                            {
                                // background: '#fafafa',
                            }
                        }
                    >
                        <div className="flex-1 mr-auto text-center">
                            <SortFiles fileType={'3D File'} />
                        </div>
                        <div className="flex-1 mr-auto text-center">
                            <SortFiles fileType={'2D File'} />
                        </div>
                    </div>

                    {parts}
                </DndProvider>
            </div>
        </React.Fragment>
    );

    return (
        <Modal
            open={PartStore.fileParts.length > 1}
            onCancel={() => {
                PartStore.fileParts.map(item => {
                    PartStore.removePart(item);
                });
            }}
            centered
            title={
                <>
                    <Title level={2} className="text-center">
                        Match your 2D and 3D files
                    </Title>
                    <Paragraph className="text-center">Drag and Drop the files between columns.</Paragraph>
                    {/* <Alert
                        showIcon
                        description="Please check that your 2D and 3D files are matching before creating a quote."
                    /> */}
                </>
            }
            width={'90vw'}
            style={{ maxWidth: '1350px' }}
            bodyStyle={{ maxHeight: '50vh', overflowY: 'scroll' }}
            footer={
                <div className="text-center my-3">
                    <Button
                        id="start-quoting-btn"
                        data-testid="otf-match-continue"
                        className="w-100"
                        style={{
                            maxWidth: 300,
                        }}
                        size={'large'}
                        type="primary"
                        loading={!!PartStore.fileParts.find(filePart => filePart.uploading)}
                        onClick={handleSubmit}
                    >
                        Continue
                    </Button>
                </div>
            }
        >
            {content}
        </Modal>
    );
});
